import { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { isIndia } from "@/lib/edition";
import { Globe, Buildings, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WORLD_COUNTRIES } from "@/lib/countries";

export default function Countries() {
  if (isIndia) return <Navigate to="/countries/India" replace />;
  return <CountriesInner />;
}

function CountriesInner() {
  useSeo("Countries", "Browse educational institutions and alumni communities from every country in the world.");
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/countries").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const filtered = (rows || []).filter((r) => r.country.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mx-auto max-w-7xl px-5 py-14" data-testid="countries-page">
      {/* Hero: intro + illustration */}
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <p className="overline text-secondary mb-3">Countries</p>
          <h1 className="font-heading text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl">
            Every country. Every institution. <span className="italic font-medium text-secondary">One global alumni community.</span>
          </h1>
          <p className="mt-5 leading-relaxed text-muted-foreground">
            AlumniPulse.net is designed with a worldwide vision: to bring schools, colleges, universities, campuses, departments, and educational communities from every country into one connected digital network.
          </p>
          <p className="mt-3 leading-relaxed text-muted-foreground">
            Whether it is a small village school with a proud 50-year history or a globally recognized university with millions of alumni, every institution has a story worth preserving and a community worth celebrating.
          </p>
          <Link to="/articles/every-country-every-institution" data-testid="countries-more-link" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline">
            More <ArrowRight size={14} />
          </Link>

          {/* World country selector */}
          <div className="mt-8 max-w-sm">
            <label className="mb-1.5 block text-sm font-medium" htmlFor="country-select">Jump to a country</label>
            <Select onValueChange={(v) => nav(`/countries/${encodeURIComponent(v)}`)}>
              <SelectTrigger id="country-select" className="rounded-full" data-testid="country-select">
                <SelectValue placeholder="Select a country…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {WORLD_COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-3 -rotate-1 rounded-[2rem] bg-secondary/10" />
          <img src="/article-covers/countries-hero.png" alt="A global network of educational institutions" className="relative w-full rounded-[1.75rem] border border-border/60 shadow-xl" data-testid="countries-hero-image" />
        </div>
      </div>

      {/* Browse countries that already have communities */}
      <div className="mt-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xl">
            <h2 className="font-heading text-2xl font-light tracking-tight">Alumni communities by country</h2>
            <p className="mt-1 text-muted-foreground">Pick a country to discover its institutions and alumni groups.</p>
          </div>
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search countries…" className="rounded-full pl-10" data-testid="country-search" />
          </div>
        </div>

        {rows === null ? <p className="mt-10 text-muted-foreground">Loading…</p>
          : filtered.length === 0 ? <p className="mt-10 text-muted-foreground">No countries yet.</p>
          : (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((r) => (
                <button key={r.country} onClick={() => nav(`/countries/${encodeURIComponent(r.country)}`)}
                  className="group rounded-2xl border border-border/60 bg-card p-6 text-left shadow-sm transition-colors hover:border-secondary/40" data-testid={`country-card-${r.country}`}>
                  <Globe size={26} weight="duotone" className="text-secondary" />
                  <h3 className="mt-3 font-heading text-lg font-medium">{r.country}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Buildings size={14} /> {r.institutions} institution{r.institutions === 1 ? "" : "s"}</p>
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
